<script setup>
import { computed } from "vue";
import ArgonProgress from "@/components/ArgonProgress.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import VehicleAvatar from "@/components/VehicleAvatar.vue";
import SimpleTable from "@/components/table/SimpleTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";

const props = defineProps({
  items: {
    type: Object,
    default: () => ({
      total: 0,
      data: [],
    }),
  },
});

const fleetHeaders = Object.freeze([
  { header: "Vehículo", field: "vehicle", position: "start" },
  { header: "Progreso", field: "stops", position: "start" },
  { header: "", field: "progress", position: "start" },
  { header: "Estado", field: "status", position: "center" },
  { header: "SLA", field: "sla", position: "center" },
]);

const tableItems = computed(() => ({
  total: props.items?.total || 0,
  data: Array.isArray(props.items?.data) ? props.items.data : [],
}));

function normalizePercent(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.min(100, Number(parsed.toFixed(2))));
}

function formatPercent(value) {
  return `${normalizePercent(value).toFixed(2)}%`;
}

function normalizeStatusKey(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function getPosition(field) {
  const column = fleetHeaders.find((item) => item.field === field);
  return column?.position || "start";
}

function formatStops(item) {
  const completed = Number(item?.stops_completed) || 0;
  const total = Number(item?.stops_total) || 0;
  return `${completed} / ${total}`;
}
</script>

<template>
  <div class="card h-100">
    <div class="card-header pb-0">
      <p class="text-xs text-uppercase text-secondary font-weight-bolder mb-0">
        Rutas
      </p>
    </div>
    <div class="card-body">
      <simple-table
        :headers="fleetHeaders"
        :items="tableItems"
        empty-message="Sin vehículos asignados para esta fecha"
        :show-pagination="false"
        :show-line-separator="true"
        :max-height="460"
        :min-height="350"
        class="dashboard-table"
      >
        <template #default="{ item }">
          <argon-td
            :position="getPosition('name')"
            :text-primary="item.vehicle_name"
            :text-secondary="item.vehicle_model"
          >
            <template #prepend>
              <vehicle-avatar
                :label="item.vehicle_name"
                :abbreviation="item.vehicle_abbreviation"
                :accent-color="item.route_color_hex"
                :size="28"
              />
            </template>
          </argon-td>

          <argon-td
            :position="getPosition('stops')"
            :text-primary="formatStops(item)"
          />

          <argon-td :position="getPosition('progress')">
            <div>
              <argon-progress
                :percentage="item.progress_percent"
                :type="normalizeStatusKey(item.current_status).toLowerCase()"
              />
              <span class="text-xxs text-primary">
                {{ formatPercent(item.progress_percent) }}
              </span>
            </div>
          </argon-td>

          <argon-td :position="getPosition('status')">
            <argon-status
              entity="route"
              :status="item.current_status"
              width="100px"
            />
          </argon-td>

          <argon-td :position="getPosition('sla')">
            <argon-status
              entity="sla"
              :status="normalizeStatusKey(item.sla_alert)"
              width="80px"
            />
          </argon-td>
        </template>
      </simple-table>
    </div>
  </div>
</template>
